// Per-user choice for the server's response capture (server/lib/responseCapture.js, telemetry
// P2c). On by default for accounts at a DEFAULT_ON_EMAIL_DOMAINS domain, off for any other. A
// choice is the user's token, the lowercase hex SHA-256 of their sub (the same hash as their
// capture prefix), for on, or CAPTURE_OPT_OUT_PREFIX + token for off. The browser keeps one
// choice per account that made one (up to MAX_CHOICES, joined by CHOICE_SEPARATOR) and the
// cookie carries them all; the server applies only the request's own user's, so a choice never
// carries over to another account signed in to the same browser (that account gets its own
// default), and one account's change never drops another's. A cookie, not a header, because
// the realtime EventSource cannot send custom headers. The cookie is the only store: every tab
// reads and writes the same one, and each change reads it afresh (setCaptureChoice), so a tab
// that has not yet seen another tab's change never writes it away. Choices once kept in
// localStorage (CAPTURE_OPT_IN_STORAGE_KEY) are merged into the cookie on each load
// (migrateLegacyChoices, hooks/useResponseCaptureOptIn.js).

import responseCaptureShared from "../../../shared/responseCapture.json";

/**
 * The choices cookie's name, from shared/responseCapture.json (owner-map REG-25), the file
 * server/lib/responseCapture.js reads too. Not the opt-in cookie of earlier builds
 * (`sooth_capture_opt_in`, still honoured by the server as an opt-in), so a tab still running
 * one of them, which writes and deletes that cookie, never touches the choices.
 */
export const CAPTURE_CHOICES_COOKIE = responseCaptureShared.choicesCookieName;

/** The cookie of earlier builds (one user's choice); read once per load, to merge it in. */
export const LEGACY_CAPTURE_COOKIE = "sooth_capture_opt_in";

/** Where earlier builds kept the choices (localStorage); read once, to move them into the cookie. */
export const CAPTURE_OPT_IN_STORAGE_KEY = "sooth-responseCaptureOptIn";

/** Must match CAPTURE_OPT_OUT_PREFIX in server/lib/responseCapture.js. */
export const CAPTURE_OPT_OUT_PREFIX = "off-";

/** Must match DEFAULT_ON_EMAIL_DOMAINS in server/lib/responseCapture.js. */
export const DEFAULT_ON_EMAIL_DOMAINS = ["sooth.inc"];

/** Must match CAPTURE_CHOICE_SEPARATOR in server/lib/responseCapture.js. */
export const CHOICE_SEPARATOR = ".";

/** Most accounts whose choices one browser keeps; the oldest choice goes first. */
export const MAX_CHOICES = 8;

/**
 * Re-set on every load. The cookie is the choices' only store, so it outlives the 30-day capture
 * retention: an opt-out must not lapse while someone is away. 400 days is browsers' cap.
 */
const COOKIE_MAX_AGE_S = 400 * 24 * 60 * 60;

/** One choice: a user's token (on), or the opt-out prefix and the token (off). */
const CHOICE = "(?:off-)?[0-9a-f]{64}";
const VALUE_RE = new RegExp(`^${CHOICE}$`);
/** A cookie value: one to MAX_CHOICES choices joined by CHOICE_SEPARATOR. */
const COOKIE_RE = new RegExp(`^${CHOICE}(?:\\.${CHOICE}){0,${MAX_CHOICES - 1}}$`);

/** The token a choice is about. */
const tokenOf = (choice) =>
  choice.startsWith(CAPTURE_OPT_OUT_PREFIX) ? choice.slice(CAPTURE_OPT_OUT_PREFIX.length) : choice;

/**
 * The well-formed choices of a stored value, at most one per account (the last one wins) and
 * at most MAX_CHOICES (the newest).
 *
 * @param {unknown} stored
 * @returns {string[]}
 */
export function captureChoices(stored) {
  if (typeof stored !== "string") return [];
  const out = [];
  for (const choice of stored.split(CHOICE_SEPARATOR)) {
    if (!VALUE_RE.test(choice)) continue;
    const i = out.findIndex((c) => tokenOf(c) === tokenOf(choice));
    if (i !== -1) out.splice(i, 1);
    out.push(choice);
  }
  return out.slice(-MAX_CHOICES);
}

/**
 * True when an account with this email is captured unless it turns capture off; the server
 * decides the same way (isCaptureDefaultOn in server/lib/responseCapture.js).
 *
 * @param {string|null|undefined} email
 * @returns {boolean}
 */
export function isCaptureDefaultOn(email) {
  if (typeof email !== "string") return false;
  const at = email.lastIndexOf("@");
  return at > 0 && DEFAULT_ON_EMAIL_DOMAINS.includes(email.slice(at + 1).toLowerCase());
}

/**
 * The user's opt-in token: lowercase hex SHA-256 of the sub's UTF-8 bytes. Null without a
 * sub or without WebCrypto (an insecure context), which leaves capture off.
 *
 * @param {string|null|undefined} sub
 * @param {SubtleCrypto|null|undefined} [subtle]
 * @returns {Promise<string|null>}
 */
export async function captureOptInToken(sub, subtle = globalThis.crypto?.subtle) {
  if (typeof sub !== "string" || !sub || !subtle) return null;
  try {
    const digest = await subtle.digest("SHA-256", new TextEncoder().encode(sub));
    return Array.from(new Uint8Array(digest), (b) => b.toString(16).padStart(2, "0")).join("");
  } catch {
    return null;
  }
}

/**
 * The `document.cookie` assignment that sets the cookie to `token`, or deletes it when
 * `token` is not a well-formed cookie value (choices, each a token or an opt-out of one).
 * `Secure` is added on https; plain-http localhost dev would otherwise drop it.
 *
 * @param {string|null} token
 * @param {boolean} secure
 * @returns {string}
 */
export function captureOptInCookieString(token, secure) {
  const attrs = `; Path=/; SameSite=Lax${secure ? "; Secure" : ""}`;
  return typeof token === "string" && COOKIE_RE.test(token)
    ? `${CAPTURE_CHOICES_COOKIE}=${token}${attrs}; Max-Age=${COOKIE_MAX_AGE_S}`
    : `${CAPTURE_CHOICES_COOKIE}=${attrs}; Max-Age=0`;
}

/**
 * Make the opt-in cookie hold `token` (or delete it for null). Never throws (no document,
 * cookies blocked).
 *
 * @param {string|null} token
 * @param {{cookie: string}|undefined} [doc]
 * @param {{protocol: string}|undefined} [loc]
 */
export function syncCaptureOptInCookie(
  token,
  doc = globalThis.document,
  loc = globalThis.location,
) {
  if (!doc) return;
  try {
    doc.cookie = captureOptInCookieString(token, loc?.protocol === "https:");
  } catch {
    /* cookies unavailable: capture stays off, which is the safe side */
  }
}

/**
 * The toggle's state for the signed-in user. `computed` is the last token hashed, tagged
 * with the sub it was hashed for; it counts only while that sub is still signed in, so a
 * previous account's token is never reused while the new one is hashing. On when the stored
 * choices hold this user's token; off when they hold this user's opt-out; else `defaultOn`.
 *
 * @param {{storedToken: unknown, computed: {sub: string|null, token: string|null},
 *          sub: string|null|undefined, defaultOn?: boolean}} args
 * @returns {{optIn: boolean, token: string|null}}
 */
export function resolveCaptureOptIn({ storedToken, computed, sub, defaultOn = false }) {
  const token = sub && computed.sub === sub ? computed.token : null;
  if (token === null) return { optIn: false, token };
  const mine = captureChoices(storedToken).find((c) => tokenOf(c) === token);
  return { optIn: mine === undefined ? defaultOn === true : mine === token, token };
}

/**
 * The stored value after the signed-in user (with `token`) sets the toggle to `on`: the other
 * accounts' choices, then theirs (their token, or their opt-out). Unchanged while the token is
 * unknown (still hashing, or no WebCrypto).
 *
 * @param {unknown} stored
 * @param {boolean} on
 * @param {string|null} token
 * @returns {string}
 */
export function withCaptureChoice(stored, on, token) {
  const others = captureChoices(stored).filter((c) => !token || tokenOf(c) !== token);
  const mine = token ? [on ? token : CAPTURE_OPT_OUT_PREFIX + token] : [];
  return [...others, ...mine].slice(-MAX_CHOICES).join(CHOICE_SEPARATOR);
}

/**
 * The capture cookie's value from `doc.cookie` (the choices cookie, or `name`), or null.
 * Never throws.
 *
 * @param {{cookie: string}|undefined} [doc]
 * @param {string} [name]
 * @returns {string|null}
 */
export function readCaptureCookie(doc = globalThis.document, name = CAPTURE_CHOICES_COOKIE) {
  try {
    const prefix = `${name}=`;
    const part = String(doc?.cookie ?? "")
      .split(/;\s*/)
      .find((p) => p.startsWith(prefix));
    return part ? part.slice(prefix.length) : null;
  } catch {
    return null;
  }
}

/**
 * The choices an earlier build stored in `storage` (JSON-encoded under
 * CAPTURE_OPT_IN_STORAGE_KEY), or "" when absent, unreadable or storage is unavailable. Never
 * throws.
 *
 * @param {{getItem: (k: string) => string|null}|null} storage
 * @returns {string}
 */
export function readStoredChoices(storage) {
  try {
    const raw = storage?.getItem(CAPTURE_OPT_IN_STORAGE_KEY) ?? null;
    const v = raw === null ? "" : JSON.parse(raw);
    return typeof v === "string" ? v : "";
  } catch {
    return "";
  }
}

/**
 * The cookie value for `storedToken`'s well-formed choices, whichever accounts they belong to,
 * or null for none.
 *
 * @param {unknown} storedToken
 * @returns {string|null}
 */
export function captureOptInCookieToken(storedToken) {
  const choices = captureChoices(storedToken);
  return choices.length ? choices.join(CHOICE_SEPARATOR) : null;
}

/**
 * The legacy stored choices folded into the cookie's. An account only one of them has keeps
 * that choice. Where they disagree, which is newer is unknown (a build wrote storage, then the
 * cookie), so the opt-out wins: migrating never turns an account's capture back on.
 *
 * @param {string} legacy  `readStoredChoices`
 * @param {string|null|undefined} cookieValue  `readCaptureCookie`
 * @returns {string}
 */
export function migrateLegacyChoices(legacy, cookieValue) {
  let out = captureChoices(legacy).join(CHOICE_SEPARATOR);
  const legacyFor = new Map(captureChoices(legacy).map((c) => [tokenOf(c), c]));
  for (const c of captureChoices(cookieValue)) {
    const off = c.startsWith(CAPTURE_OPT_OUT_PREFIX);
    const was = legacyFor.get(tokenOf(c));
    const keepLegacyOff = !off && was !== undefined && was.startsWith(CAPTURE_OPT_OUT_PREFIX);
    if (!keepLegacyOff) out = withCaptureChoice(out, !off, tokenOf(c));
  }
  return out;
}

/**
 * Set the signed-in user's choice (with `token`) to `on` in the cookie: read it afresh, merge
 * this user's choice in (`withCaptureChoice`) and write it back. Returns the cookie's value as
 * it now reads ("" for none), so a blocked write shows as the state it left. Not a cross-tab
 * lock: two changes in two tabs at the same instant can still lose one. Never throws.
 *
 * @param {boolean} on
 * @param {string|null} token
 * @param {{cookie: string}|undefined} [doc]
 * @param {{protocol: string}|undefined} [loc]
 * @returns {string}
 */
export function setCaptureChoice(on, token, doc = globalThis.document, loc = globalThis.location) {
  const next = withCaptureChoice(readCaptureCookie(doc), on, token);
  syncCaptureOptInCookie(captureOptInCookieToken(next), doc, loc);
  return readCaptureCookie(doc) ?? "";
}

/**
 * The choices cookie's choices, plus an earlier build's (`legacy`) for each account the
 * cookie has none for, in the room its own choices leave. Only this build writes the choices
 * cookie, so where it has a choice it is the newer one, and a legacy opt-out merged on every
 * load never overrides a later opt-in.
 *
 * @param {string|null|undefined} choicesCookie  `readCaptureCookie`
 * @param {string} legacy  an earlier build's choices (`migrateLegacyChoices` of its two stores)
 * @returns {string}
 */
export function withLegacyChoices(choicesCookie, legacy) {
  const out = captureChoices(choicesCookie);
  const have = new Set(out.map(tokenOf));
  // Only the room the current choices leave: a legacy entry never evicts one of them.
  const room = MAX_CHOICES - out.length;
  const fill = captureChoices(legacy).filter((c) => !have.has(tokenOf(c)));
  return [...out, ...fill.slice(Math.max(0, fill.length - room))].join(CHOICE_SEPARATOR);
}
